import { InputType } from 'config'
import { calculateSum, getPrice3decimal, removeComma, solveDecimalJavascriptSum } from 'utils'

import { normalizeDrawType } from '../../Budget/constants'
import { configuredAmount } from './configuredBudget'
import { isOpeningBalanceRow } from './openingBalance'

/**
 * What a draw is measured against: the loan's construction ledger as it stands, the draw type the
 * budget is set to, and the two budget figures the net-eligible rule reads.
 */
export interface DrawCalculationContext {
  /** The loan's construction (trust) ledger rows, in the order the tab holds them. */
  rows: Array<Record<string, any>>
  /** Budget draw type. Blank falls through to Reimbursement Pro Rata, as it does on the tab. */
  drawType: string
  /** -1 (product hides the field) or 0 (exposed but left blank) when the loan carries no rehab
   * budget. Either way there is no ratio to apply and Net follows Gross. */
  rehabBudget: number
  constructionReserve: number
  /** The row being edited, so the prior-row sums stop at it. Omitted for a new draw. */
  rowId?: number
}

/**
 * Whether a money field has been filled in at all, as opposed to filled in with zero.
 *
 * `removeComma` cannot tell the two apart - it answers 0 for both - so this reads the raw value.
 */
const isAnswered = (value: unknown): boolean => String(value ?? '').trim() !== ''

/** The row's own total: its fees and its payment, which do not depend on Gross. */
const applyTotalDraw = (inputs: Record<string, InputType>) => {
  inputs['totalDraw'].value = calculateSum([
    inputs.inspectionFee.value,
    inputs.wireFee.value,
    inputs.titleUpdateFee.value,
    inputs.additionHoldback.value,
    inputs.payment.value,
    inputs.deposit.value,
  ])
}

/**
 * The Construction Reserve draw calculation: eligibility in, recommended disbursement out.
 *
 * Lifted out of `ConstructionReserveTab` so the loan file and the pipeline's Draw modal run the
 * SAME arithmetic rather than two copies of it. They are the same screen as far as the servicer
 * is concerned, and a draw entered from one has to land identically to one entered from the
 * other; two implementations of a rule this fiddly would not stay identical for long.
 *
 * Mutates and returns `inputs`, the way the tab's own `inputLogic` does, so it can be dropped
 * into either caller's change handler unchanged.
 *
 * The chain:
 * - **Previously Disbursed** walks every earlier row, netting deposits off payments and adding
 *   the fees and holdback that left the reserve with them. Opening-balance rows are skipped:
 *   they fund the bucket rather than draw on it.
 * - **Net Eligible** follows the draw type. Advancement and Reimbursement Prior to Delta pass
 *   Gross straight through; Reimbursement After Delta holds Net at zero until Gross clears
 *   (Rehab Budget - Construction Reserve), the share the borrower funds first; Pro Rata, the
 *   default, scales Gross by the reserve's share of the budget. With no rehab budget - whether the
 *   product type hides it or it was simply left blank, see `configuredAmount` - there is no ratio
 *   to apply, so Gross carries through rather than leaving Net pinned at $0.
 * - **Recommended Disbursement** is Net less what the reserve has already paid out and less this
 *   row's own fees and holdback.
 *
 * `eligibleAmount` ("Gross Eligible Amount") is the **cumulative** cost incurred to date, not the
 * increment for one draw, and every rule above ends by subtracting Previously Disbursed, which is
 * cumulative too. The two only net out to a single draw if Gross is cumulative as well. A row
 * holding an increment reads plausibly for the first draw or two, then goes negative as soon as
 * what the reserve has paid out exceeds one draw's size - which is where the servicer meets it,
 * opening a requested draw to process it.
 *
 * Which is also why nothing derived from Gross is stated until a Gross has actually been entered.
 * Blank reads as 0 through `removeComma`, so the chain used to answer a question nobody had asked
 * yet: Net 0, and Net less Previously Disbursed for New Eligible and Max Eligible Disbursement -
 * on a loan part way through its budget, a large negative, sitting in two read-only fields with an
 * Autofill link beside them offering to pay it. The ledger's cumulative figures are facts and stay
 * on show; the ones that need a Gross wait for one. A Gross of 0 is a real answer and is honoured,
 * so this turns on the field being EMPTY rather than on it being zero.
 */
export const applyDrawCalculation = (
  inputs: Record<string, InputType>,
  ctx: DrawCalculationContext,
  key?: string,
): Record<string, InputType> => {
  const { rows, drawType, rehabBudget, constructionReserve, rowId } = ctx

  // A draw and a deposit are opposite directions; entering one clears the other so a row can
  // never claim to be both.
  if (key === 'payment') inputs['deposit'].value = 0
  if (key === 'deposit') inputs['payment'].value = 0

  let previouslyDisbursed = 0
  for (let i = 0; i < rows.length; i += 1) {
    if (rows[i].id === rowId) break
    if (isOpeningBalanceRow(rows[i])) continue
    previouslyDisbursed = solveDecimalJavascriptSum([
      previouslyDisbursed,
      removeComma(rows[i].payment),
      -1 * removeComma(rows[i].deposit),
      removeComma(rows[i].inspectionFee),
      removeComma(rows[i].wireFee),
      removeComma(rows[i].titleUpdateFee),
      removeComma(rows[i].additionHoldback),
    ])
  }
  inputs['previouslyDisbursed'].value = previouslyDisbursed.toFixed(2)

  // Nothing has been claimed yet, so there is nothing to work out. The row's own total still
  // stands - it is the fees and the payment, which owe nothing to Gross.
  if (!isAnswered(inputs['eligibleAmount'].value)) {
    inputs['netEligibleAmount'].value = ''
    inputs['grossEligibleDrawAmount'].value = ''
    inputs['recommendedDisbursement'].value = ''
    // Dropped rather than left alone: the inputs are cloned forward on every change, so a link
    // built on an earlier pass would still be hanging there, still holding that pass's figure.
    ;(inputs['payment'] as any).additionalElements = undefined
    applyTotalDraw(inputs)
    return inputs
  }

  const grossEligible = removeComma(inputs['eligibleAmount'].value)
  const resolvedDrawType = normalizeDrawType(drawType)
  // Both figures are asked about through `configuredAmount`, so a budget the product type hides
  // and one a servicer never filled in reach every rule below as the same thing: no budget.
  const budget = configuredAmount(rehabBudget)
  const reserve = configuredAmount(constructionReserve)
  let netEligibleAmountNum = grossEligible

  if (budget > 0) {
    if (resolvedDrawType === 'Advancement' || resolvedDrawType === 'Reimbursement Prior to Delta') {
      netEligibleAmountNum = grossEligible
    } else if (resolvedDrawType === 'Reimbursement After Delta') {
      // The delta comes straight off this row's Gross, because Gross is already the cumulative
      // cost. Summing the earlier rows' Gross on top of it counted every prior draw's cost a
      // second time, and since the result is then reduced by Previously Disbursed anyway, the
      // double count landed in the servicer's recommended disbursement.
      const delta = solveDecimalJavascriptSum([budget, -1 * reserve])
      netEligibleAmountNum = Math.max(0, solveDecimalJavascriptSum([grossEligible, -1 * delta]))
    } else {
      // A zero divisor cannot get here any more. A reserve of 0 against a real budget still gives
      // 0, which is the right answer rather than a missing one: the lender is financing none of it.
      netEligibleAmountNum = (grossEligible * reserve) / budget
    }
  }

  const netEligibleAmount = netEligibleAmountNum.toFixed(2)
  inputs['netEligibleAmount'].value = netEligibleAmount

  let recommendedDisbursement = removeComma(netEligibleAmount)
  let feeApplied = false
  for (let i = 0; i < rows.length; i += 1) {
    if (rows[i].id === rowId) {
      recommendedDisbursement = solveDecimalJavascriptSum([
        recommendedDisbursement,
        -1 * removeComma(inputs['inspectionFee'].value),
        -1 * removeComma(inputs['wireFee'].value),
        -1 * removeComma(inputs['titleUpdateFee'].value),
        -1 * removeComma(inputs['additionHoldback'].value),
      ])
      feeApplied = true
      break
    }
    if (isOpeningBalanceRow(rows[i])) continue
    recommendedDisbursement = solveDecimalJavascriptSum([
      recommendedDisbursement,
      -1 * removeComma(rows[i].payment),
      removeComma(rows[i].deposit),
      -1 * removeComma(rows[i].inspectionFee),
      -1 * removeComma(rows[i].wireFee),
      -1 * removeComma(rows[i].titleUpdateFee),
      -1 * removeComma(rows[i].additionHoldback),
    ])
  }
  if (!feeApplied) {
    recommendedDisbursement = solveDecimalJavascriptSum([
      recommendedDisbursement,
      -1 * removeComma(inputs['inspectionFee'].value),
      -1 * removeComma(inputs['wireFee'].value),
      -1 * removeComma(inputs['titleUpdateFee'].value),
      -1 * removeComma(inputs['additionHoldback'].value),
    ])
  }
  inputs['recommendedDisbursement'].value = recommendedDisbursement.toFixed(2)

  inputs['grossEligibleDrawAmount'].value = getPrice3decimal(
    solveDecimalJavascriptSum([
      removeComma(inputs['netEligibleAmount'].value),
      -1 * removeComma(inputs['previouslyDisbursed'].value),
    ]),
  )

  inputs['payment'].additionalElements = ((onChange: Function) => {
    return (
      <span
        className="mx-2 text-shade-blue hover:underline cursor-pointer font-variation-settings-600"
        onClick={() => onChange(recommendedDisbursement)}
      >
        Autofill
      </span>
    )
  }) as any

  applyTotalDraw(inputs)

  return inputs
}

/** The recommended disbursement on its own, for a caller that wants to seed the amount with it. */
export const recommendedDrawAmount = (inputs: Record<string, InputType>): number =>
  removeComma(inputs['recommendedDisbursement']?.value)
